import { z } from 'zod';
import { UuidSchema } from './uuid';

export const PushPlatformFamilySchema = z.enum([
  'ios',
  'android',
  'desktop',
  'unknown',
]);

export const PushPermissionStateSchema = z.enum([
  'default',
  'denied',
  'granted',
]);

export const QueueReminderChannelSchema = z.literal('web_push');

export const QueueReminderStateSchema = z.object({
  requested: z.boolean(),
  channel: z.union([QueueReminderChannelSchema, z.null()]),
  subscription_id: UuidSchema.nullable(),
  requested_at: z.string().nullable(),
  sent_at: z.string().nullable(),
});

export const PushSubscriptionSummarySchema = z.object({
  subscription_id: UuidSchema,
  app_install_id: UuidSchema,
  device_label: z.string().nullable(),
  platform_family: PushPlatformFamilySchema,
  queue_notifications_enabled: z.boolean(),
  marketing_notifications_enabled: z.boolean(),
  disabled_at: z.string().nullable(),
});

export const PushBootstrapResponseSchema = z.object({
  web_push_enabled: z.boolean(),
  vapid_public_key: z.string().nullable(),
  queue_reminders_enabled: z.boolean(),
  marketing_push_enabled: z.boolean(),
  subscriptions: z.array(PushSubscriptionSummarySchema),
});

export const UpsertPushSubscriptionKeysSchema = z.object({
  p256dh: z.string().min(1),
  auth: z.string().min(1),
});

export const UpsertPushSubscriptionRequestSchema = z.object({
  app_install_id: UuidSchema,
  endpoint: z.string().url(),
  keys: UpsertPushSubscriptionKeysSchema,
  permission_state: PushPermissionStateSchema,
  platform_family: PushPlatformFamilySchema,
  device_label: z.string().trim().min(1).optional(),
});

export const UpsertPushSubscriptionResponseSchema = z.object({
  subscription_id: UuidSchema,
  app_install_id: UuidSchema,
  queue_notifications_enabled: z.boolean(),
  marketing_notifications_enabled: z.boolean(),
  disabled_at: z.string().nullable(),
});

export const DeletePushSubscriptionResponseSchema = z.object({
  success: z.boolean(),
});

export const SetQueueReminderRequestSchema = z.object({
  ticket_id: UuidSchema,
  subscription_id: UuidSchema,
  channel: QueueReminderChannelSchema,
});

export const ClearQueueReminderRequestSchema = z.object({
  ticket_id: UuidSchema,
});

export type PushPlatformFamily = z.infer<typeof PushPlatformFamilySchema>;
export type PushPermissionState = z.infer<typeof PushPermissionStateSchema>;
export type QueueReminderChannel = z.infer<typeof QueueReminderChannelSchema>;
export type QueueReminderState = z.infer<typeof QueueReminderStateSchema>;
export type PushSubscriptionSummary = z.infer<
  typeof PushSubscriptionSummarySchema
>;
export type PushBootstrapResponse = z.infer<
  typeof PushBootstrapResponseSchema
>;
export type UpsertPushSubscriptionRequest = z.infer<
  typeof UpsertPushSubscriptionRequestSchema
>;
export type UpsertPushSubscriptionResponse = z.infer<
  typeof UpsertPushSubscriptionResponseSchema
>;
export type DeletePushSubscriptionResponse = z.infer<
  typeof DeletePushSubscriptionResponseSchema
>;
export type SetQueueReminderRequest = z.infer<
  typeof SetQueueReminderRequestSchema
>;
export type ClearQueueReminderRequest = z.infer<
  typeof ClearQueueReminderRequestSchema
>;
